/* =====================================================================
   Alex & Lace — AMBIENT SECTION PARALLAX (ROADMAP §932, "Text parallax
   across homepage sections"). GSAP + ScrollTrigger. Sibling to the hero's
   al-hero-motion.js — kept separate so the hero module stays hero-scoped.

   MECHANIC (LOCKED, built 2026-07-17): the SUBTLE ambient text drift — the
   same voice as the hero's mid-preset parallax at LOWER VOLUME. As each
   editorial section transits the viewport, its TEXT layer drifts vertically
   (leads UP on exit) while its IMAGE layer stays put — "text moving
   independently of imagery." TEXT-ONLY (images static, owner-approved): the
   section image frames are object-fit:cover with no bleed budget, so drifting
   the image would expose a frame edge — the text drift alone reads the depth
   with zero edge risk and zero layout impact.

   Targets (both pre-seamed in their sections):
     [data-al-row-head]  — al-product-row heading block  → drift 0.06·vh peak-to-peak
     [data-al-duo-text]  — al-image-duo frosted captions → drift 0.05·vh peak-to-peak
   Symmetric drift (±ratio/2·vh): the text sits at its natural position when the
   section is centred (where the eye is), leading up as it exits. Transform only
   → zero CLS. Range = the section's full transit (top bottom → bottom top),
   scrubbed on scrub:0.9 (rides Lenis's lerped scroll, so it reads on the
   --al-ease family, same as the hero drifts).

   GATING: desktop ≥1024 + prefers-reduced-motion:no-preference, via
   gsap.matchMedia() — which auto-reverts every transform below when the query
   stops matching. So mobile + reduced-motion render the identical STATIC layout,
   and JS-off never runs this at all → all three are the same static page.
   ===================================================================== */

(() => {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;

  // element selector → drift ratio (fraction of viewport, peak-to-peak, symmetric).
  const TARGETS = [
    { sel: '[data-al-row-head]', ratio: 0.06 },
    { sel: '[data-al-duo-text]', ratio: 0.05 },
    // NEUTRAL drift alias (additive 2026-07-24) — intent-named hook so any page (About, future) can opt
    // into the same lower-volume text drift without borrowing a "row"/"duo" attribute. Calm 0.05 ratio,
    // same voice. Text-only, so the images-static / zero-edge-risk invariant is unchanged. No-op where
    // absent (homepage never emits it), so existing behaviour is untouched.
    { sel: '[data-al-ambient-drift]', ratio: 0.05 },
  ];
  if (!TARGETS.some((t) => document.querySelector(t.sel))) return; // no editorial bands here

  gsap.registerPlugin(ScrollTrigger);

  const SCRUB = 0.9;
  const vh = () => window.innerHeight;
  const mm = gsap.matchMedia();

  mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
    TARGETS.forEach(({ sel, ratio }) => {
      document.querySelectorAll(sel).forEach((el) => {
        // Trigger on the whole section's transit — .shopify-section wraps every section (the row is a
        // custom element, not a <section>); fall back to the nearest section-ish ancestor.
        const section = el.closest('.shopify-section') || el.closest('al-product-row, .al-duo, section') || el;
        gsap.fromTo(
          el,
          { y: () => (ratio / 2) * vh() },
          {
            y: () => -(ratio / 2) * vh(),
            ease: 'none',
            scrollTrigger: {
              trigger: section,
              start: 'top bottom',
              end: 'bottom top',
              scrub: SCRUB,
              invalidateOnRefresh: true,
            },
          }
        );
      });
    });
  });

  // Late-layout refresh (CLAUDE verification guardrail): the Nohemi webfont swap and Lenis's async
  // boot (html.lenis body{height:auto}) both change layout AFTER these triggers are created; recompute
  // bounds on fonts.ready, on 'load', and once Lenis signals ready. Warm-cache / no-Lenis = cheap no-ops.
  const refreshST = () => ScrollTrigger.refresh();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refreshST);
  window.addEventListener('load', refreshST);
  window.addEventListener('al:lenis-ready', refreshST);
})();
